import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  ApprovalStatus,
  DocumentCategory,
  StatusCode,
} from '../../../../../api/enumerations';
import {
  approveContractDocument,
  approveNoticeCompanyDocument,
  approveNoticeCompanyDocumentWithReservation,
  cancelExtraDocument,
  getNoticeCompany,
  getNoticeCompanyDocuments,
  getNoticeCompanyTeam,
  getNoticeCompanyTerms,
  listNoticeCompanyChangeRequests,
  rejectContractDocument,
  rejectNoticeCompanyDocument,
  replaceNoticeCompanyDocument,
} from '../../../../../api/theHive/notices';
import { ChangeRequestStatus } from '../../../../../api/theHive/notices/enumerations';
import {
  AdministrativeTermData,
  ApproveWithReservationData,
  CancelExtraDocumentData,
  ChangeRequestData,
  NoticeCompanyTeamData,
  NoticeCompanyTeamGroup,
  NoticeDocumentsData,
  RejectDocumentData,
  RequestNoticeCompany,
} from '../../../../../api/theHive/notices/types';
import { Constants } from '../../../../../constants/hiveNotices';
import { matchCompanySubdomain } from '../../../../../helpers';
import { useStoragedJwt } from '../../../../../hooks/useDecodedJwt';
import useErrorMessage from '../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../hooks/useSnackbar';
import {
  getFirstPendingTeamMemberId,
  getTeamCriteriaGroups,
  hasAdministrativeDocuments,
  hasPendingEngineeringAnalysis,
  isContractPendingReview,
  teamStatusFilter,
} from './utils';

type LatLngLiteral = google.maps.LatLngLiteral;

export type AnalysisTabKind = 'juridico' | 'engenharia' | 'administrativo';

export type EngineeringScope = 'team' | 'company' | 'unavailable';

export interface DocumentSection {
  name: string;
  docList: NoticeDocumentsData[];
}

interface AnalysisTab {
  kind: AnalysisTabKind;
  label: string;
  sections: DocumentSection[];
  role: number | null;
}

export interface UseLicenseRequestHook {
  loading: boolean;
  docLoading: boolean;
  companyData: RequestNoticeCompany | undefined;
  noticeId: number;
  requestId: number;
  extraDocs: NoticeDocumentsData[];
  pendingChangeRequests: ChangeRequestData[];
  administrativeTerms: AdministrativeTermData[];
  teamData: NoticeCompanyTeamData | undefined;
  engineeringScope: EngineeringScope;
  accreditedCriteriaGroups: NoticeCompanyTeamGroup[];
  initiallyExpandedTeamMemberId: number | undefined;
  analysisTabs: AnalysisTab[];
  currentTabKind: AnalysisTabKind | undefined;
  activeSections: DocumentSection[];
  handleChangeTab: (kind: AnalysisTabKind) => void;
  isDiscredited: boolean;
  documentActionsDisabled: boolean;
  hasOpenChangeRequest: boolean;
  statusAlertMessage: string | undefined;
  hasCompanyCoords: boolean;
  searchMap: boolean;
  pinPlace: LatLngLiteral;
  setPinPlace: (place: LatLngLiteral) => void;
  navigateBack: () => void;
  handleLicenseRequest: () => Promise<void>;
  handleApproveDocument: (docId?: number) => Promise<void>;
  handleApproveWithReservation: (
    reservationNote: string,
    docId: number
  ) => Promise<void>;
  handleReplaceDocument: (docId: number) => Promise<void>;
  handleRejectDocument: (reason: string, docId?: number) => Promise<void>;
  handleCancelExtraDocument: (docId: number) => Promise<void>;
  handleApproveTeamDocument: (docId?: number) => Promise<void>;
  handleApproveTeamDocumentWithReservation: (
    reservationNote: string,
    docId: number
  ) => Promise<void>;
  handleReplaceTeamDocument: (docId: number) => Promise<void>;
  handleRejectTeamDocument: (reason: string, docId?: number) => Promise<void>;
}

// RBAC for the analysis tabs (REAL-3099): Legal → legal (17); Engineering →
// engineer (2). Platform admin (3) and bank admin (10) see both. The full role
// enum and the access helper live on the RBAC branch; here the values are used
// directly to keep this branch self-contained.
const roleAdmin = 3;
const roleBankAdmin = 10;
const roleBankResponsavelTecnico = 14;
const roleJuridico = 17;

export default function useLicenseRequest(): UseLicenseRequestHook {
  const [loading, setLoading] = useState(true);
  const [docLoading, setDocLoading] = useState(false);
  const [companyData, setCompanyData] = useState<RequestNoticeCompany>();

  const [legalDocs, setLegalDocs] = useState<NoticeDocumentsData[]>([]);
  const [legalPersonDocs, setLegalPersonDocs] = useState<NoticeDocumentsData[]>(
    []
  );
  const [extraDocs, setExtraDocs] = useState<NoticeDocumentsData[]>([]);
  const [pendingChangeRequests, setPendingChangeRequests] = useState<
    ChangeRequestData[]
  >([]);
  const [administrativeTerms, setAdministrativeTerms] = useState<
    AdministrativeTermData[]
  >([]);
  const [teamData, setTeamData] = useState<NoticeCompanyTeamData>();
  const [teamLoadFailed, setTeamLoadFailed] = useState(false);

  const [activeTabKind, setActiveTabKind] = useState<AnalysisTabKind>();
  const [initiallyExpandedTeamMemberId, setInitiallyExpandedTeamMemberId] =
    useState<number>();

  const [searchMap, setSearchMap] = useState(true);
  const [pinPlace, setPinPlace] = useState<LatLngLiteral>({
    lat: -23.56162,
    lng: -46.65591,
  });

  const navigate = useNavigate();
  const { pathname, key } = useLocation();
  const { notice: noticeIdParam, request, company } = useParams();
  const noticeId = parseInt(noticeIdParam as string, 10);
  const requestId = parseInt(request as string, 10);
  const companyId = parseInt(company as string, 10);
  const { getErrorMessage } = useErrorMessage();
  const { handleSnackbar } = useSnackbar();

  const hasCompanyCoords = Boolean(
    companyData?.company.latitude && companyData?.company.longitude
  );

  const isPendingStatus =
    companyData?.approval_status === ApprovalStatus.PENDING;
  const isAwaitingFormalization =
    companyData?.approval_status === ApprovalStatus.AWAITING_FORMALIZATION;
  const isFormalizationSubmitted =
    companyData?.approval_status === ApprovalStatus.FORMALIZATION_SUBMITTED;
  const isApprovedStatus =
    companyData?.approval_status === ApprovalStatus.APPROVED;
  const isDiscredited =
    companyData?.approval_status === ApprovalStatus.DISCREDITED;

  const showFormalizationTables = hasAdministrativeDocuments(
    companyData?.approval_status
  );

  const documentActionsDisabled = !isPendingStatus || isDiscredited;

  const canAdvanceToFormalization =
    isPendingStatus && companyData?.advance?.can_advance === true;

  const getStatusAlertMessage = (): string | undefined => {
    if (canAdvanceToFormalization) return Constants.awaitingProviderAdvance;
    if (isAwaitingFormalization) return Constants.awaitingSignedContract;
    if (
      isFormalizationSubmitted &&
      isContractPendingReview(companyData?.signed_contract)
    )
      return Constants.formalizationUnderReview;
    return undefined;
  };

  const statusAlertMessage = getStatusAlertMessage();

  const navigateBack = useCallback((): void => {
    if (key !== 'default') {
      navigate(-1);
      return;
    }
    if (pathname.includes('company-details') && companyId) {
      navigate(`/the-hive/licensed/company-details/${companyId}`);
    } else {
      navigate(`/the-hive/notices/${noticeId}`);
    }
  }, [key, navigate, pathname, companyId, noticeId]);

  const handleTeamData = useCallback(async (): Promise<
    NoticeCompanyTeamData | undefined
  > => {
    const teamResp = await getNoticeCompanyTeam({
      noticeCompanyId: requestId,
      status: teamStatusFilter,
    });

    if (
      teamResp instanceof Error ||
      teamResp.detail.description ||
      !teamResp.data
    ) {
      setTeamLoadFailed(true);
      handleSnackbar(Constants.teamLoadError, true);
      return undefined;
    }

    setTeamLoadFailed(false);
    setTeamData(teamResp.data);
    return teamResp.data;
  }, [requestId, handleSnackbar]);

  const handleTeamRefresh = useCallback(async (): Promise<void> => {
    const response = await getNoticeCompany(requestId);

    if (!(response instanceof Error) && response.data) {
      setCompanyData(response.data);
    }

    await handleTeamData();
  }, [requestId, handleTeamData]);

  const handleLicenseRequest = useCallback(
    async (loadPage = false): Promise<void> => {
      if (loadPage) setLoading(true);
      try {
        const response = await getNoticeCompany(requestId);

        if (response.detail.description)
          throw new Error(response.detail.description);

        if (!response.data) throw new Error(Constants.genericError);

        setCompanyData(response.data);

        const resp = await getNoticeCompanyDocuments(requestId);

        if (resp.detail.description) throw new Error(resp.detail.description);

        if (!resp.data) throw new Error(Constants.genericError);

        const documents = resp.data;
        const byCategory = (
          category: DocumentCategory
        ): NoticeDocumentsData[] =>
          documents.filter((data) => data.document_category === category);

        setLegalDocs(byCategory(DocumentCategory.LEGAL));
        setLegalPersonDocs(byCategory(DocumentCategory.TECHNICAL));
        setExtraDocs(byCategory(DocumentCategory.OTHER));

        // Request scope (poles and criteria groups). Unpaginated on purpose:
        // reads the whole list. Supplementary — a failure here must not block
        // the page.
        const changeRequestsResp = await listNoticeCompanyChangeRequests({
          noticeCompanyId: requestId,
          status: [ChangeRequestStatus.PENDING],
        });
        const changeRequests =
          !(changeRequestsResp instanceof Error) && changeRequestsResp.data
            ? changeRequestsResp.data
            : [];
        setPendingChangeRequests(changeRequests);

        const [termsResp, team] = await Promise.all([
          hasAdministrativeDocuments(response.data.approval_status)
            ? getNoticeCompanyTerms(requestId)
            : Promise.resolve(undefined),
          handleTeamData(),
        ]);
        setAdministrativeTerms(
          termsResp && !(termsResp instanceof Error) && termsResp.data
            ? termsResp.data
            : []
        );

        // Only on the page load: the reloads that follow each document
        // decision must not pull the analyst out of the tab they are on.
        if (loadPage) {
          const openEngineering = hasPendingEngineeringAnalysis(
            response.data.approval_status,
            team,
            changeRequests
          );
          setActiveTabKind(openEngineering ? 'engenharia' : undefined);
          setInitiallyExpandedTeamMemberId(
            openEngineering ? getFirstPendingTeamMemberId(team) : undefined
          );
        }
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
        navigateBack();
      } finally {
        setLoading(false);
      }
    },
    [requestId, handleTeamData, handleSnackbar, getErrorMessage, navigateBack]
  );

  useEffect(() => {
    if (noticeId) handleLicenseRequest(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noticeId, requestId]);

  useEffect(() => {
    if (!companyData) return undefined;

    const { latitude, longitude } = companyData.company;
    if (latitude && longitude) {
      setPinPlace({ lat: latitude, lng: longitude });
      return undefined;
    }

    const delayDebounceFn = setTimeout(
      () => setSearchMap((prev) => !prev),
      500
    );

    return () => clearTimeout(delayDebounceFn);
  }, [companyData]);

  const withDocLoading = useCallback(
    async (
      fn: () => Promise<{
        detail: { description?: string; status_code: number };
      }>,
      successMsg: string,
      onSuccess?: () => void | Promise<void>
    ): Promise<void> => {
      setDocLoading(true);
      try {
        const response = await fn();
        if (response.detail.description)
          throw new Error(response.detail.description);
        if (response.detail.status_code !== StatusCode.OK)
          throw new Error(Constants.genericError);
        handleSnackbar(successMsg, false);
        await (onSuccess ? onSuccess() : handleLicenseRequest());
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      } finally {
        setDocLoading(false);
      }
    },
    [handleSnackbar, getErrorMessage, handleLicenseRequest]
  );

  const handleApproveDocument = useCallback(
    async (docId?: number, refresh?: () => Promise<void>): Promise<void> => {
      const successMessage = docId
        ? Constants.approveDocumentSuccess
        : Constants.approveAccreditationSuccess;
      const onSuccess = docId ? refresh : navigateBack;
      await withDocLoading(
        () =>
          docId
            ? approveNoticeCompanyDocument(requestId, docId)
            : approveContractDocument(requestId),
        successMessage,
        onSuccess
      );
    },
    [requestId, navigateBack, withDocLoading]
  );

  const handleApproveWithReservation = useCallback(
    async (
      reservationNote: string,
      docId: number,
      refresh?: () => Promise<void>
    ): Promise<void> => {
      const data: ApproveWithReservationData = {
        reservation_note: reservationNote,
      };
      await withDocLoading(
        () =>
          approveNoticeCompanyDocumentWithReservation(requestId, docId, data),
        Constants.approveWithReservationSuccess,
        refresh
      );
    },
    [requestId, withDocLoading]
  );

  const handleReplaceDocument = useCallback(
    async (docId: number, refresh?: () => Promise<void>): Promise<void> => {
      await withDocLoading(
        () => replaceNoticeCompanyDocument(requestId, docId),
        Constants.replaceRequestedSuccess,
        refresh
      );
    },
    [requestId, withDocLoading]
  );

  const handleRejectDocument = useCallback(
    async (
      reason: string,
      docId?: number,
      refresh?: () => Promise<void>
    ): Promise<void> => {
      const data: RejectDocumentData = { rejection_reason: reason };
      await withDocLoading(
        () =>
          docId
            ? rejectNoticeCompanyDocument(requestId, docId, data)
            : rejectContractDocument(requestId, data),
        Constants.rejectDocumentSuccess,
        refresh
      );
    },
    [requestId, withDocLoading]
  );

  const handleCancelExtraDocument = useCallback(
    async (docId: number): Promise<void> => {
      const data: CancelExtraDocumentData = {
        noticeCompanyId: requestId,
        documentId: docId,
      };
      await withDocLoading(
        () => cancelExtraDocument(data),
        Constants.cancelDocumentSuccess
      );
    },
    [requestId, withDocLoading]
  );

  const handleApproveTeamDocument = useCallback(
    (docId?: number): Promise<void> =>
      handleApproveDocument(docId, handleTeamRefresh),
    [handleApproveDocument, handleTeamRefresh]
  );

  const handleApproveTeamDocumentWithReservation = useCallback(
    (reservationNote: string, docId: number): Promise<void> =>
      handleApproveWithReservation(reservationNote, docId, handleTeamRefresh),
    [handleApproveWithReservation, handleTeamRefresh]
  );

  const handleReplaceTeamDocument = useCallback(
    (docId: number): Promise<void> =>
      handleReplaceDocument(docId, handleTeamRefresh),
    [handleReplaceDocument, handleTeamRefresh]
  );

  const handleRejectTeamDocument = useCallback(
    (reason: string, docId?: number): Promise<void> =>
      handleRejectDocument(reason, docId, handleTeamRefresh),
    [handleRejectDocument, handleTeamRefresh]
  );

  const currentRole = useStoragedJwt()?.user.companies.find(
    (item) => item.subdomain === matchCompanySubdomain()
  )?.role;
  const isAdmin = currentRole === roleAdmin || currentRole === roleBankAdmin;

  // `kind` decides the tab's content; `role` decides who sees it. The
  // administrative tab (contract + acceptances) has `role: null`: it used to
  // sit loose at the end of the page, visible to any role — moving it into a
  // tab does not restrict it. It only shows up from formalization onwards,
  // otherwise it would be empty.
  const analysisTabs: AnalysisTab[] = [
    {
      kind: 'juridico' as const,
      label: Constants.tabLegalAnalysis,
      sections: [
        { name: Constants.legalAnalysisSection, docList: legalDocs },
        {
          name: Constants.legalPersonAnalysisSection,
          docList: legalPersonDocs,
        },
      ],
      role: roleJuridico,
    },
    {
      // Engineering has no company tables any more: each person on the
      // team brings their own criteria groups and "Outro documento…", coming
      // from criteria-documents under their scope.
      kind: 'engenharia' as const,
      label: Constants.tabEngineeringAnalysis,
      sections: [],
      role: roleBankResponsavelTecnico,
    },
    ...(showFormalizationTables
      ? [
          {
            kind: 'administrativo' as const,
            label: Constants.tabAdministrative,
            sections: [],
            role: null,
          },
        ]
      : []),
  ].filter(
    (tab) =>
      tab.role === null ||
      isAdmin ||
      (currentRole != null && tab.role === currentRole)
  );

  const hasOpenChangeRequest =
    isApprovedStatus && pendingChangeRequests.length > 0;

  // Never fall back to the COMPANY scope on a roster we could not read: with
  // no trustworthy team the tab says so instead of showing a screen that
  // looks complete. A stale roster still beats no roster.
  const getEngineeringScope = (): EngineeringScope => {
    if (teamLoadFailed && !teamData) return 'unavailable';
    return teamData?.items.length ? 'team' : 'company';
  };

  const engineeringScope = getEngineeringScope();

  // The chosen tab may not exist for this role (or may not have been chosen
  // yet): falls back to the first available one.
  const currentTabKind = analysisTabs.some((tab) => tab.kind === activeTabKind)
    ? activeTabKind
    : analysisTabs[0]?.kind;
  const activeSections =
    analysisTabs.find((tab) => tab.kind === currentTabKind)?.sections ?? [];

  const handleChangeTab = useCallback(
    (kind: AnalysisTabKind): void => setActiveTabKind(kind),
    []
  );

  return {
    loading,
    docLoading,
    companyData,
    noticeId,
    requestId,
    extraDocs,
    pendingChangeRequests,
    administrativeTerms,
    teamData,
    engineeringScope,
    accreditedCriteriaGroups: getTeamCriteriaGroups(teamData),
    initiallyExpandedTeamMemberId,
    analysisTabs,
    currentTabKind,
    activeSections,
    handleChangeTab,
    isDiscredited,
    documentActionsDisabled,
    hasOpenChangeRequest,
    statusAlertMessage,
    hasCompanyCoords,
    searchMap,
    pinPlace,
    setPinPlace,
    navigateBack,
    handleLicenseRequest,
    handleApproveDocument,
    handleApproveWithReservation,
    handleReplaceDocument,
    handleRejectDocument,
    handleCancelExtraDocument,
    handleApproveTeamDocument,
    handleApproveTeamDocumentWithReservation,
    handleReplaceTeamDocument,
    handleRejectTeamDocument,
  };
}
